import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, useTheme } from '@mui/material';
import axios from 'axios';
import { DateTime } from 'luxon';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// Color scale: red (<70), yellow (70-89), green (90+)
function getCellColor(score) {
  if (score === null || score === undefined) return '#ececec';
  if (score >= 90) return '#4caf50'; // green
  if (score >= 70) return '#ffeb3b'; // yellow
  return '#f44336'; // red
}

export default function AdminTechHeatmap() {
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState([]);
  const [data, setData] = useState([]);
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const sunday = new Date(today);
    sunday.setHours(0, 0, 0, 0);
    sunday.setDate(today.getDate() - today.getDay());
    return sunday;
  });
  const theme = useTheme();

  // Helper: format week range for display
  const getWeekRangeString = () => {
    const start = new Date(weekStart);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  };

  // Fetch data for the selected week
  useEffect(() => {
    setLoading(true);
    const startStr = weekStart.toISOString().slice(0, 10);
    axios.get(`${API_BASE_URL}/api/submissions/heatmap/week?start=${startStr}`)
      .then(res => {
        setDays(res.data.days);
        setData(res.data.data);
      })
      .finally(() => setLoading(false));
  }, [weekStart]);

  // Navigation handlers
  const goToPrevWeek = () => {
    setWeekStart(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() - 7);
      return d;
    });
  };
  const goToNextWeek = () => {
    setWeekStart(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + 7);
      return d;
    });
  };
  const goToCurrentWeek = () => {
    const today = new Date();
    const sunday = new Date(today);
    sunday.setHours(0, 0, 0, 0);
    sunday.setDate(today.getDate() - today.getDay());
    setWeekStart(sunday);
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /> </Box>;
  if (!days || !Array.isArray(days) || !days.length || !data || !Array.isArray(data)) {
    return <Typography sx={{ mt: 4 }}>No data for this week.</Typography>;
  }

  return (
    <Box sx={{ width: '100%', overflowX: 'auto', mt: 3 }}>
      {/* Week Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 2 }}>
        <button onClick={goToPrevWeek} style={{ padding: '6px 14px', fontWeight: 700, borderRadius: 6, border: '1px solid #bbb', background: '#fff', cursor: 'pointer' }}>← Prev</button>
        <button onClick={goToCurrentWeek} style={{ padding: '6px 14px', fontWeight: 700, borderRadius: 6, border: '1px solid #bbb', background: '#fff', cursor: 'pointer' }}>Current Week</button>
        <button onClick={goToNextWeek} style={{ padding: '6px 14px', fontWeight: 700, borderRadius: 6, border: '1px solid #bbb', background: '#fff', cursor: 'pointer' }}>Next →</button>
        <Typography variant="body1" sx={{ ml: 3, fontWeight: 600 }}>
          {getWeekRangeString()}
        </Typography>
      </Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, textAlign: 'center' }}>
        Technician Performance Heatmap (Week-to-Date)
      </Typography>
      {/* Legend */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2, justifyContent: 'center' }}>
        <Box sx={{ width: 28, height: 18, bgcolor: '#4caf50', borderRadius: 1, border: '1px solid #888' }} />
        <Typography variant="body2">90%+</Typography>
        <Box sx={{ width: 28, height: 18, bgcolor: '#ffeb3b', borderRadius: 1, border: '1px solid #888' }} />
        <Typography variant="body2">70-89%</Typography>
        <Box sx={{ width: 28, height: 18, bgcolor: '#f44336', borderRadius: 1, border: '1px solid #888' }} />
        <Typography variant="body2">Below 70%</Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: `180px repeat(${(days || []).length}, 1fr)`, border: '1px solid #ccc', borderRadius: 2, overflow: 'hidden' }}>
        {/* Header Row */}
        <Box sx={{ bgcolor: theme.palette.grey[200], fontWeight: 700, p: 1, borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', textAlign: 'left' }}>Technician</Box>
        {(days || []).map(day => {
          // Use Luxon to parse as America/Chicago, so weekday labels are correct
          const local = DateTime.fromISO(day, { zone: 'America/Chicago' });
          return (
            <Box key={day} sx={{ bgcolor: theme.palette.grey[200], fontWeight: 700, p: 1, borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', textAlign: 'center' }}>
              {local.toFormat('ccc')}
            </Box>
          );
        })}
        {/* Data Rows */}
        {(data || []).map(row => (
          <React.Fragment key={row.techId}>
            <Box sx={{ fontWeight: 600, p: 1, borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', textAlign: 'left', bgcolor: theme.palette.grey[100] }}>{row.techName}</Box>
            {(days || []).map(day => (
              <Box key={day} sx={{ width: '100%', height: 36, bgcolor: getCellColor(row[day]), borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc' }} />
            ))}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}
